import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography, Slider } from "@mui/material";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useDeleteProjectMutation, useGetProjectsQuery } from "./api/projectsApi";
import { Project } from "./types/projects";
import AddEditProject from "./components/AddEditProjects";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";



const ProjectManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const navigate = useNavigate();

  // Using RTK Query to fetch projects
  const { data: projects, isLoading, isError, isFetching } = useGetProjectsQuery();
const [deleteProject]=useDeleteProjectMutation();

  const columns: Array<MRT_ColumnDef<Project>> = [
    {
      header: "Title",
      accessorKey: "title",
      size: 100,
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 150,
      Cell({ row: { original } }) {
        const description = original?.description || '';
        const truncated = description.length > 100
          ? description.slice(0, 50) + '...'
          : description;
        return <span>{truncated}</span>;
      },
    },
    {
      header: "Location",
      accessorKey: "location",
      size: 150,
      Cell({ row: { original } }) {
        const location = original?.location || '';
        const truncated = location.length > 50
          ? location.slice(0, 50) + '...'
          : location;
        return <span>{truncated}</span>;
      },
    },
    {
      header: "Visit Price",
      accessorKey: "visit_price",
      size: 80,
      Cell({ row: { original } }) {
        return (
          <Typography>
            {original.visit_price ? original.visit_price.toLocaleString() : '0'} ETB
          </Typography>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "construction_status",
      size: 50,
      Cell({ row: { original } }): JSX.Element {
        const statusMap = {
          "Under Construction": { color: "warning", text: "Under Construction" },
          // Add other statuses if needed
          "Completed": { color: "success", text: "Completed" },
          "Upcoming": { color: "info", text: "Upcoming" }
        };
        
        const status = statusMap[original.construction_status as keyof typeof statusMap] || 
                      { color: "default", text: original.construction_status || 'Unknown' };
        return (
          <Chip 
            color={status.color as any} 
            label={status.text}
            variant="outlined"
          />
        );
      },
    },
    {
      header: "Progress",
      accessorKey: "progress",
      size: 100,
      Cell({ row: { original } }): JSX.Element {
        if (original.construction_status !== 'In Progress') {
          return <Typography>-</Typography>;
        }
        return (
          <Box sx={{ width: '100%', px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Slider
                value={original.progress || 0}
                disabled
                aria-label="Progress"
                valueLabelDisplay="auto"
                step={1}
                marks
                min={0}
                max={100}
                size="small"
                sx={{ flex: 1 }}
              />
              <Typography variant="body2" sx={{ minWidth: '45px' }}>
                {original.progress || 0}%
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      header: "Amenities",
      accessorKey: "amenities",
      size: 150,
      Cell({ row: { original } }): JSX.Element {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {original.amenities && original.amenities.slice(0,3).map((amenity, index) => (
              <Chip key={index} label={amenity?.replace(/"/g, '') || ''} size="small" />
            ))}
            {original.amenities && original.amenities.length > 3 && (
              <Chip label={`+${original.amenities.length - 3} more`} size="small" variant="outlined" />
            )}
          </Box>
        );
      },
    },
    {
      header: "Unit Types",
      accessorKey: "unit_types",
      size: 150,
      Cell({ row: { original } }): JSX.Element {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {original.unit_types && original.unit_types.map((unit, index) => (
              <Chip key={index} label={unit?.name || 'Unknown'} size="small" />
            ))}
          </Box>
        );
      },
    },

{
  header: "Image",
  accessorKey: "project_image",
  size: 80,
  Cell: ({ row: { original } }) => (
    original.project_image ? (
      <img
        src={original.project_image}
        alt={original.title}
        style={{
          width: 60,
          height: 60,
          objectFit: "cover",
          borderRadius: 8,
          boxShadow: "0 0 4px rgba(0,0,0,0.2)",
        }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    ) : (
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 8,
          backgroundColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'grey.500',
          fontSize: '12px'
        }}
      >
        No Image
      </Box>
    )
  ),
}

   
  ];

  return (
    <Box>
      <DataTable<Project>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Projects</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${projects?.data?.length??0} Projects`} 
              />
            </Box>
            <Button
              onClick={() =>{
                setSelectedProject(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Project
            </Button>
          </Box>
        }
        list={projects?.data??[]}
        count={projects?.data?.length??0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProject(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProject(row);
                // setOpenEditDialog(true);
                setOpenDialog(true)
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${row.id}`);
              }}
            >
              <RemoveRedEye />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => navigate(`/projects/${row.id}`)}
        rowNumberMode={undefined}
      />

      {/* Add Project Dialog */}
      <AddEditProject 
        open={openDialog} 
        project={selectedProject}
        setModalOpen={setOpenDialog} 
      />

      {/* Confirm Delete Modal */}
      {selectedProject && (
        <ConfirmModal
          type="warning"
          title="Delete Project"
          message={`Are you sure you want to delete this '${selectedProject.title}' project?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteProject(selectedProject.id.toString());
            setOpenConfirmModal(false);
            setSelectedProject(null);
          }}
        />
      )}
    </Box>
  );
};

export default ProjectManager;